import * as Sentry from "@sentry/browser";
import "core-js/stable";
import "regenerator-runtime/runtime";
import i18next from "i18next";

import { SENTRY_DSN } from "./config.js";
import { formatAddress } from "./helpers.js";
import { i18nPromise } from "./i18n";
import { getMobileMoneyProvider } from "./mobileMoneyProviders.js";
import * as model from "./model.js";
import addressView from "./views/addressView";
import autocompleteView from "./views/autocompleteView";
import cartView from "./views/cartView.js";
import deliveryView from "./views/deliveryView.js";
import mainView from "./views/mainView.js";
import productView from "./views/productView.js";
import resultsItemView from "./views/resultsItemView.js";
import searchItemView from "./views/searchItemView.js";
import "../css/normalize.css";
import "../css/vendor-webflow.css";
import "../css/yenda.css";

/* eslint-disable */
Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [
    // Send console.log, console.warn, and console.error calls as logs to Sentry
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],
  // Enable logs to be sent to Sentry
  enableLogs: true,
});
/* eslint-enable */

// Return array from object
const imgBlockArray = Array.from(productView._imgBlock);

// Get ID of all food items
let idProductArray = imgBlockArray.map((el) =>
  Number(el.getAttribute("data-id")),
);

// Create a new array with non-null elements
idProductArray = idProductArray.filter((el) => el);

const getModalParamConfig = () => [
  {
    overlay: document.querySelector(".overlay-container.item"),
    params: ["product-id"],
  },
  {
    overlay: document.querySelector(".overlay-container.checkout"),
    params: ["openCart"],
  },
  {
    overlay: document.querySelector(".overlay-container.service"),
    params: ["openCart"],
  },
];

const isOverlayVisible = (overlay) => {
  if (!overlay) {
    return false;
  }

  const style = window.getComputedStyle(overlay);
  return style.display !== "none";
};

const removeModalParams = (paramsToRemove) => {
  const NO_PARAMS = 0;

  if (!Array.isArray(paramsToRemove) || paramsToRemove.length === NO_PARAMS) {
    return;
  }

  const url = new URL(window.location.href);

  paramsToRemove.forEach((param) => {
    url.searchParams.delete(param);
  });

  history.replaceState(null, "", url.toString());
};

const removeModalParamsIfUnused = (params, modalConfig) => {
  const remainingParams = params.filter((param) =>
    modalConfig.some(
      ({ overlay, params: configParams }) =>
        configParams.includes(param) && isOverlayVisible(overlay),
    ),
  );

  const paramsToRemove = params.filter(
    (param) => !remainingParams.includes(param),
  );

  removeModalParams(paramsToRemove);
};

const initModalUrlCleanup = () => {
  const modalConfig = getModalParamConfig();

  modalConfig.forEach(({ overlay, params }) => {
    if (!overlay) {
      return;
    }

    const observer = new MutationObserver(() => {
      if (!isOverlayVisible(overlay)) {
        removeModalParamsIfUnused(params, modalConfig);
      }
    });

    observer.observe(overlay, {
      attributeFilter: ["style", "class"],
      attributes: true,
    });
  });
};

// Autocomplete address based on input value
export const autoCompleteAddress = async function (event) {
  const NO_RESULT_COUNT = 0;

  // Get input value
  const inputValue = event.target.value.trim().toLowerCase();

  // Clear previous results if input is empty
  if (!inputValue.length) {
    autocompleteView._clear();
    return;
  }

  try {
    // Get addresses based on input value
    await model.loadAutocompleteAddress(inputValue);

    const { addressList, addressValidationReasons } = model.state;
    const hasAddresses =
      Array.isArray(addressList) && addressList.length > NO_RESULT_COUNT;
    const validationKey = model.getAddressValidationKey(
      addressValidationReasons,
    );

    // Keep DeliveryView's submit-time validation in sync with the latest lookup result
    deliveryView.setLookupState(hasAddresses, validationKey);

    if (hasAddresses) {
      autocompleteView.renderData(addressList);
    } else {
      // Clear if no addresses are found
      autocompleteView._clear();
    }
  } catch {
    autocompleteView.renderError();
  }
};

const controlUpdateCart = async function () {
  // Get state
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");
  try {
    // Get ID of store
    const ID_STORE_LOCATION = mainView.getStoreLocationId();

    // 1) Find required modifiers
    const requiredModifier = productView.findRequiredModifier();

    // 2) Add product to cart only if required modifiers are filled
    if (!requiredModifier || productView.renderUpdateCart(requiredModifier)) {
      // Clone object
      const productCartClone = { ...model.state.productCart };

      // 3) Update product
      model.updateProduct(productCartClone);

      // 4) Update product in cart
      await model.updateProductCart(productCartClone, ID_STORE_LOCATION);

      // 5) Get cart content
      idMode === "pickup"
        ? await model.loadCart("pickup", ID_STORE_LOCATION)
        : await model.loadCart("delivery", ID_STORE_LOCATION);

      // 6) Update cart count
      model.updateCartCount();

      // 7) Uncheck inputs
      productView.uncheckInput();

      // 8) Render cart
      cartView.renderData(model.state.cart, idMode);

      // 9) Attach event listener to product in cart
      productView.renderOnClickUpdate(controlProductOnClickUpdate);
      cartView.handlerRemoveCart(controlRemoveCart);
    }
  } catch {
    productView.renderUpdateCartError();
  }
};

// Open food modal on image click
const controlProductOnClickUpdate = function (event) {
  // Prevent form submission
  event.preventDefault();

  // Get index of food item
  const index = event.target
    .closest(".box-shaddow-item")
    .querySelector(".image-item")
    .getAttribute("data-index");

  if (index) {
    try {
      // 1) Show the spinner immediately
      productView.showLoadingSpinner();

      // 2) Loading product
      model.loadProductCart(index);

      // 3) Render product ingredients
      productView.renderDataUpdate(model.state.productCart);

      // Hide the spinner
      productView.hideLoadingSpinner();

      // 4) Render modal with product image
      productView.renderModal(model.state.productCart.imageUrl);

      // 5) Render filled inputs
      model.fillInputs(model.state.productCart.ingredients);

      // 6) Render updated item price
      productView.updateItemPrice();

      // 7) Validate Modifier on update
      productView.validateModifierSelection();

      // 8) Render updated modifier
      productView.limitModifierSelection();

      cartView.handlerUpdateCart(controlUpdateCart);
      productView.closeModalUpdateItem();
      productView.closeBckgrndItem();
    } catch {
      productView.renderError();

      // Hide the spinner
      productView.hideLoadingSpinner();
    }
  }
};

const controlRemoveCart = async function (event) {
  // Get state
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");
  try {
    // Get ID of store
    const ID_STORE_LOCATION = mainView.getStoreLocationId();

    // Get index of food item
    const index = event.target
      .closest(".box-shaddow-item")
      .querySelector(".image-item")
      .getAttribute("data-index");

    // 1) Delete product from cart
    await model.removeCartProduct(index, ID_STORE_LOCATION);

    // 2) Get cart content
    idMode === "pickup"
      ? await model.loadCart("pickup", ID_STORE_LOCATION)
      : await model.loadCart("delivery", ID_STORE_LOCATION);

    // 3) Update Cart count
    model.updateCartCount();

    // 4) Render cart
    cartView.renderClearCart(model.state.cart, idMode);

    // 5) Attach event listener to products in cart
    productView.renderOnClickUpdate(controlProductOnClickUpdate);
    cartView.handlerRemoveCart(controlRemoveCart);
  } catch {
    productView.renderRemoveCartError();
  }
};

/* eslint-disable require-atomic-updates */

// Prevent duplicate orders by locking the handler while an order request is in progress
let isSubmitting = false;

const buildOrderPayload = function ({
  isLoggedIn,
  orderType,
  paymentMethod,
  userInfo,
}) {
  const displayName = isLoggedIn
    ? `${userInfo.firstName} ${userInfo.lastName}`
    : userInfo.name;

  const MIN_TIP = 0;
  const tipText = document.querySelector(".cart-tip")?.textContent ?? "";
  const tip = cartView.extractNumber(tipText) || MIN_TIP;

  const { fulfillmentEndTime, fulfillmentStartTime } =
    cartView.getFulfillmentWindow();

  const customerDetails = {
    email: userInfo.email,
    name: displayName,
    phoneNumber: userInfo.phoneNumber,
    ...(orderType === "delivery"
      ? {
          addressLine1: userInfo.address,
          addressLine2: userInfo.apartmentNumber,
          city: userInfo.city || "",
        }
      : {}),
  };

  return {
    customerDetails,
    fulfillmentEndTime,
    fulfillmentStartTime,
    paymentMethod,
    tip,
  };
};

const controlCreateOrder = async function (event) {
  // Prevent form submission
  event.preventDefault();

  // Get state
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");
  const orderType = idMode === "pickup" ? "pickup" : "delivery";
  const paymentMethod = cartView.getSelectedPaymentMethod();

  // Prevent double-submit
  if (isSubmitting) {
    return;
  }
  isSubmitting = true;

  try {
    // Get ID of store
    const ID_STORE_LOCATION = mainView.getStoreLocationId();

    // 1) Detect auth state
    const isLoggedIn = cartView.isUserAuthenticatedFromDOM();

    // 2) Extract user data
    const userInfo = isLoggedIn
      ? model.state.loggedInUserInfo
      : model.state.defaultUserInfo;

    if (!userInfo) {
      throw new Error("User information is not available");
    }

    // 3) Build the payload shared by normal orders and mobile-money sessions
    const orderPayload = buildOrderPayload({
      isLoggedIn,
      orderType,
      paymentMethod,
      userInfo,
    });

    const mobileMoneyProvider = getMobileMoneyProvider(paymentMethod);

    if (mobileMoneyProvider && !mobileMoneyProvider.enabled) {
      cartView.renderCheckoutError(
        i18next.t(mobileMoneyProvider.unavailableMessageKey),
      );
      return;
    }

    // 4) Show loading state on button
    cartView.showCheckoutButtonLoading();

    if (mobileMoneyProvider) {
      // 5) Mobile-money: validate the checkout and open a payment session — no order yet
      const sessionToken = await model.createCheckoutSession(
        orderPayload,
        orderType,
        ID_STORE_LOCATION,
      );

      if (!sessionToken) {
        console.error(
          "Server accepted checkout but returned no session token.",
        );

        throw new Error("Unable to start payment. Please try again.");
      }

      // 6) Send the customer to collect payment; the order is created on confirmation.
      // Derived from the current path rather than hardcoded, because the checkout and
      // the payment page are siblings on whichever origin is serving this storefront:
      // /store/{slug}/order -> /store/{slug}/mobile-money-payment on the platform host,
      // /order -> /mobile-money-payment on the restaurant's own domain. Staying on the
      // serving origin is what keeps the session cookie — and so the session's owner
      // key — valid on the page the customer lands on.
      const paymentPath = window.location.pathname.replace(
        /[^/]*$/u,
        "mobile-money-payment",
      );
      const url = new URL(paymentPath, window.location.origin);
      url.searchParams.set("session", sessionToken);

      // Redirect without adding a history entry (so Back won’t return to checkout)
      window.location.replace(url.toString());
      return;
    }

    // 5) Create order (pay-on-pickup / pay-on-delivery)
    const orderId = await model.createOrder(
      orderPayload,
      orderType,
      ID_STORE_LOCATION,
    );

    if (!orderId) {
      console.error("Server accepted order but returned no ID.");

      throw new Error("Order confirmation failed. Check your order history.");
    }

    // Build confirmation URL with store identifier from current path
    // Current path: /store/{storeIdentifier}/order -> Confirmation: /store/{storeIdentifier}/confirmation
    const currentPath = window.location.pathname;
    const confirmationPath = currentPath.replace('/order', '/confirmation');
    const url = new URL(confirmationPath, window.location.origin);
    url.searchParams.set("orderId", String(orderId));
    url.searchParams.set("locationId", String(ID_STORE_LOCATION));

    // Redirect without adding a history entry (so Back won�t return to checkout)
    window.location.replace(url.toString());
  } catch (err) {
    // Extract structured error information
    const orderError = err?.orderError;

    // Use specific error message if available, otherwise fallback to default
    const errorMessage = orderError
      ? i18next.t(orderError.userMessageKey)
      : i18next.t("error.checkoutFailed");

    cartView.renderCheckoutError(errorMessage);
    cartView.hideCheckoutButtonLoading();
  } finally {
    isSubmitting = false;
  }
};

/* eslint-enable require-atomic-updates */

const controlAddCart = async function () {
  const HIDE_TIMEOUT_MS = 4000;

  // Get state
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");
  try {
    // Get ID of store
    const ID_STORE_LOCATION = mainView.getStoreLocationId();

    // 1) Find required modifiers
    const requiredModifier = productView.findRequiredModifier();

    // Add product to cart only if required modifiers are filled
    if (!requiredModifier || productView.renderAddToCart(requiredModifier)) {
      // Clone object
      model.state.productClone = { ...model.state.product };

      const { productClone } = model.state;

      // 1) Store total price in cloned object
      productClone.priceTotal = productView._itemPriceTotal;

      // 2) Set product quantity from ".item-count"
      productClone.quantity = document.querySelector(".item-count").textContent;

      // 3) Set instructions
      productClone.instructions = document
        .querySelector(".special-instruction-inputfield")
        .value.trim();

      // 4) Set ingredients
      model.getCheckedValue(productClone);

      if (idMode === "pickup") {
        // 5) Add product to cart
        await model.addToCart(productClone, ID_STORE_LOCATION, "pickup");

        // 6) Get cart content
        await model.loadCart("pickup", ID_STORE_LOCATION);
      } else {
        // 5) Add product to cart
        await model.addToCart(productClone, ID_STORE_LOCATION, "delivery");

        // 6) Get cart content
        await model.loadCart("delivery", ID_STORE_LOCATION);
      }

      // 7) Update cart count
      model.updateCartCount();

      // 8) Uncheck inputs
      productView.uncheckInput();

      // 9) Render cart
      cartView.renderData(model.state.cart, idMode);

      // 10) Open Modal success
      cartView.renderCartSuccess();

      // 11) Hide Modal success after some time
      setTimeout(() => {
        cartView.closeCartModalSuccess();
      }, HIDE_TIMEOUT_MS);

      // 11) Attach event listener to product in cart
      productView.renderOnClickUpdate(controlProductOnClickUpdate);
      cartView.handlerRemoveCart(controlRemoveCart);
    }
  } catch {
    productView.renderAddToCartError();
  }
};

// Open food modal on image click
export const controlProductOnClick = async function (event) {
  // Prevent form submission
  event.preventDefault();

  // Get service mode
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");

  // Get ID of food item
  const id = event.target
    .closest(".item-image-block")
    .querySelector(".image-item")
    .getAttribute("data-id");

  if (id) {
    try {
      // 1) Show the spinner immediately
      productView.showLoadingSpinner();

      // 2) Loading product
      idMode === "pickup"
        ? await model.loadProduct(id, "pickup")
        : await model.loadProduct(id, "delivery");

      // 3) Render product info
      productView.renderData(model.state.product);

      // Hide the spinner
      productView.hideLoadingSpinner();

      // 4) Render modal with product image
      productView.renderModal(model.state.product.imageUrl);

      // 5) Render updated item price
      productView.updateItemPrice();

      // 6) Render updated modifier
      productView.limitModifierSelection();

      cartView.handlerAddCart(controlAddCart);
      productView.closeModalAddItem();
      productView.closeBckgrndItem();
    } catch {
      productView.renderError();

      // Hide the spinner
      productView.hideLoadingSpinner();
    }
  }
};

// This function manipulates the state of the delivery/pickup button on the main page
const toggleService = function () {
  // Get service mode
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");

  // Show modal only if ID of food item is in list of ID
  if (idMode === "pickup") {
    mainView.updatePickupButton();
    cartView.updatePickupLabel();
    cartView.renderFulfillmentHelper("pickup");
  } else {
    mainView.updateDeliveryButton();
    cartView.updateDeliveryLabel();
    cartView.renderFulfillmentHelper("delivery");
  }
};

const enforceFulfillmentMode = function () {
  const storeContext = document.querySelector("#store-context");
  if (!storeContext) {
    return false;
  }

  const parseBoolAttr = (value, fallback = true) => {
    if (typeof value === "string") {
      return value.toLowerCase() === "true";
    }
    return fallback;
  };

  const hasPickup = parseBoolAttr(storeContext.dataset.hasPickup);
  const hasDelivery = parseBoolAttr(storeContext.dataset.hasDelivery);

  const parsedUrl = new URL(window.location.href);
  const currentMode = parsedUrl.searchParams.get("mode");

  // If both modes are supported, allow any mode
  if (hasPickup && hasDelivery) {
    return false;
  }

  // Determine the only supported mode
  let supportedMode = null;
  if (hasPickup && !hasDelivery) {
    supportedMode = "pickup";
  } else if (hasDelivery && !hasPickup) {
    supportedMode = "delivery";
  }

  // If current mode doesn't match supported mode, redirect
  if (supportedMode && currentMode !== supportedMode) {
    parsedUrl.searchParams.set("mode", supportedMode);
    window.location.href = parsedUrl.toString();
    return true;
  }

  return false;
};

const initFulfillmentSelector = function () {
  const storeContext = document.querySelector("#store-context");
  if (!storeContext) {
    return;
  }

  const parseBoolAttr = (value, fallback = true) => {
    if (typeof value === "string") {
      return value.toLowerCase() === "true";
    }
    return fallback;
  };

  const hasPickup = parseBoolAttr(storeContext.dataset.hasPickup);
  const hasDelivery = parseBoolAttr(storeContext.dataset.hasDelivery);

  let fulfillmentMode = "both";
  if (hasPickup && hasDelivery) {
    fulfillmentMode = "both";
  } else if (hasPickup) {
    fulfillmentMode = "pickup";
  } else if (hasDelivery) {
    fulfillmentMode = "delivery";
  }

  const applyFulfillmentUi = (container, toggle) => {
    if (!container || !toggle) {
      return;
    }

    container.dataset.fulfillmentMode = fulfillmentMode;

    if (fulfillmentMode === "both") {
      return;
    }

    const singleLabel =
      fulfillmentMode === "pickup"
        ? i18next.t("fulfillment.pickupOnly")
        : i18next.t("fulfillment.deliveryOnly");

    toggle.innerHTML = "";

    const singlePill = document.createElement("span");
    singlePill.className = "single-fulfillment";
    singlePill.textContent = singleLabel;
    singlePill.setAttribute("role", "status");
    singlePill.setAttribute("aria-live", "polite");

    toggle.appendChild(singlePill);
  };

  const desktopContainer = document.querySelector("#fulfillment-selector");
  const desktopToggle = desktopContainer?.querySelector(".toggle-button");
  applyFulfillmentUi(desktopContainer, desktopToggle);

  const mobileContainer = document.querySelector(
    "#fulfillment-selector-mobile",
  );
  const mobileToggle = mobileContainer?.querySelector(
    ".service-toggle-button-tablet",
  );
  applyFulfillmentUi(mobileContainer, mobileToggle);
};

// Open food modal on page load
// This function just manipulates the state
const controlProductOnLoad = async function (event) {
  // Prevent form submission if event exists
  if (event) {
    event.preventDefault();
  }

  // Get ID of food item
  const parsedUrl = new URL(window.location.href);
  const idProduct = Number(parsedUrl.searchParams.get("product-id"));
  const idMode = parsedUrl.searchParams.get("mode");

  // Show modal only if ID of food item is in list of ID
  if (idProductArray.includes(idProduct)) {
    try {
      // 1) Show the spinner immediately
      productView.showLoadingSpinner();

      // 2) Loading product
      idMode === "pickup"
        ? await model.loadProduct(idProduct, "pickup")
        : await model.loadProduct(idProduct, "delivery");

      // 3) Render product info
      productView.renderData(model.state.product);

      // Hide the spinner
      productView.hideLoadingSpinner();

      // 4) Render modal with product image
      productView.renderModal(model.state.product.imageUrl);

      // 5) Render updated item price
      productView.updateItemPrice();

      // 6) Render updated modifier
      productView.limitModifierSelection();

      cartView.handlerAddCart(controlAddCart);
      productView.closeModalAddItem();
      productView.closeBckgrndItem();
    } catch {
      productView.renderError();

      // Hide the spinner
      productView.hideLoadingSpinner();
    }
  }
};

// Get store openings on page load
const getStoreOpenings = async function () {
  // Get service mode
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");

  try {
    // Get ID of store
    const ID_STORE_LOCATION = mainView.getStoreLocationId();

    idMode === "pickup"
      ? await model.loadStoreLocation(ID_STORE_LOCATION, "pickup")
      : await model.loadStoreLocation(ID_STORE_LOCATION, "delivery");
  } catch {
    mainView.renderError();
  }
};

// Update user info (works for both guest and logged-in users)
export const controlUpdateCheckout = async function () {
  // 1) Detect auth state
  const isLoggedIn = cartView.isUserAuthenticatedFromDOM();

  // 2) Save the latest form values to the model
  model.updateUserInfo(isLoggedIn);

  // 3) Extract user data & normalize name
  const userInfo = isLoggedIn
    ? model.state.loggedInUserInfo
    : model.state.defaultUserInfo;

  const displayName = isLoggedIn
    ? `${userInfo.firstName} ${userInfo.lastName}`
    : userInfo.name;

  // 4) Get service mode from URL
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");

  // Persist and sync delivery address for authenticated users
  const userContextElement = document.querySelector("#user-context");
  if (isLoggedIn && idMode !== "pickup") {
    const addressLine1 = userInfo.address?.trim() ?? "";
    const addressLine2 = userInfo.apartmentNumber?.trim() ?? "";
    const city = userInfo.city?.trim() ?? "";
    const hasAddress = Boolean(addressLine1);

    // Only persist when the user actually provided an address
    if (hasAddress) {
      try {
        await model.saveUserAddress({ addressLine1, addressLine2, city });
      } catch (err) {
        // Capture and send the error to Sentry
        Sentry.captureException(err);

        addressView.renderError();
      }
    }

    if (hasAddress) {
      const userContextJson =
        userContextElement.getAttribute("data-user-context");

      if (userContextJson) {
        try {
          const parsedContext = JSON.parse(userContextJson);
          const updatedContext = {
            ...parsedContext,
            addressLine1,
            addressLine2,
            city,
          };
          const serializedContext = JSON.stringify(updatedContext);
          userContextElement.setAttribute(
            "data-user-context",
            serializedContext,
          );
          userContextElement.dataset.userContext = serializedContext;
        } catch (err) {
          // Capture and send the error to Sentry
          Sentry.captureException(err);

          addressView.renderError();
        }
      }
    }
  }

  // 5) Render logic based on mode
  if (idMode === "pickup") {
    // Render updated pickup address
    cartView.renderPickupAddress(displayName, userInfo.phoneNumber);
  } else {
    // Format address with city (city may be undefined, formatAddress handles it)
    const fullAddress = formatAddress(userInfo.address, userInfo.city);

    // Render updated delivery address
    cartView.renderDeliveryAddress(
      displayName,
      fullAddress,
      userInfo.apartmentNumber,
      userInfo.phoneNumber,
    );
  }

  // 6) Render delivery time
  cartView.renderDeliveryTime(
    model.state.userTime,
    model.state.openings.intervalDeliveryInMinutes,
    model.state.openings.closingDays,
    model.state.openings.deliveryTimes,
    model.state.openings.timezone,
  );
};

// Open cart modal on load
// This function just manipulates the state
const controlInitCheckout = async function () {
  // Get state of cart
  const parsedUrl = new URL(window.location.href);
  const idCart = parsedUrl.searchParams.get("openCart");
  const idMode = parsedUrl.searchParams.get("mode");

  // Show modal only if cart state is true
  if (idCart === "true") {
    try {
      // Detect auth state
      const isLoggedIn = cartView.isUserAuthenticatedFromDOM();

      // Load user info
      if (isLoggedIn) {
        // Load authenticated user data from the DOM
        model.loadLoggedInUserFromDOM();
      }

      // Determine which user info to use
      const userInfoToRender = isLoggedIn
        ? model.state.loggedInUserInfo
        : model.state.defaultUserInfo;

      // Validate required fields based on mode
      let hasRequiredFields;
      if (isLoggedIn) {
        hasRequiredFields =
          userInfoToRender &&
          userInfoToRender.firstName &&
          userInfoToRender.lastName &&
          userInfoToRender.phoneNumber &&
          userInfoToRender.email &&
          (idMode === "pickup" ? true : userInfoToRender.address);
      } else {
        hasRequiredFields =
          userInfoToRender &&
          userInfoToRender.name &&
          userInfoToRender.phoneNumber &&
          userInfoToRender.email &&
          (idMode === "pickup" ? true : userInfoToRender.address);
      }

      // Handle mode-specific flow
      if (hasRequiredFields) {
        // Render user details based on mode
        if (idMode === "pickup") {
          // Render user details for pickup
          deliveryView.renderDataPickup(userInfoToRender);
        } else {
          // Render user details for delivery
          deliveryView.renderDataDelivery(userInfoToRender);

          // Autocomplete delivery address
          autocompleteView.handlerSearchAddress(autoCompleteAddress);
        }

        // Initialize submit button on page load
        deliveryView.initSubmitBtn();

        deliveryView.toggleSubmitButton();

        // Update pickup/delivery details, then render the updated data in the checkout summary
        await controlUpdateCheckout();

        // Open checkout modal
        cartView.openModalCheckout();
      } else {
        if (idMode === "pickup") {
          // Render user details for pickup
          deliveryView.renderDataPickup(userInfoToRender);

          // Render service modal
          deliveryView.openModalServicePickup();
        } else {
          // Render user details for delivery
          deliveryView.renderDataDelivery(userInfoToRender);

          // Render service modal
          deliveryView.openModalServiceDelivery();

          // Autocomplete delivery address
          autocompleteView.handlerSearchAddress(autoCompleteAddress);
        }

        // Initialize submit button on page load
        deliveryView.initSubmitBtn();

        deliveryView.toggleSubmitButton();
      }
    } catch (err) {
      // Capture and send the error to Sentry
      Sentry.captureException(err);

      mainView.renderError();
    }
  }
};

// Search product in menu
// This function just manipulates the state
const controlSearchItem = async function () {
  try {
    // Get ID of store
    const ID_STORE_LOCATION = mainView.getStoreLocationId();

    // 1) Get search query
    const query = searchItemView.getQuery();

    if (!query) {
      return;
    }

    // 2) Load search results
    await model.loadSearchItem(query, ID_STORE_LOCATION);

    resultsItemView.renderSpinner();

    // 3) Render results
    resultsItemView.renderData(model.state.searchItem.results);

    // 4) Open modal on product click
    productView.renderOnClick(controlProductOnClick);
  } catch {
    resultsItemView.renderError();
  }
};

// Search product in menu on page load
// This function just manipulates the state
const controlSearchItemOnLoad = async function () {
  // Check for parameter in URL
  const parsedUrl = new URL(window.location.href);
  const params = new URLSearchParams(parsedUrl.search);
  try {
    if (params.has("search")) {
      // Get ID of store
      const ID_STORE_LOCATION = mainView.getStoreLocationId();

      // 1) Get search query
      const query = searchItemView.getQueryOnLoad();

      if (!query) {
        return;
      }

      resultsItemView.renderSpinner();

      // 2) Load search results
      await model.loadSearchItem(query, ID_STORE_LOCATION);

      // 3) Render results
      resultsItemView.renderData(model.state.searchItem.results);

      // 4) Open modal on product click
      productView.renderOnClick(controlProductOnClick);
    }
  } catch {
    resultsItemView.renderError();
  }
};

// Update delivery time
export const controlUpdateDeliveryTime = function () {
  // Update delivery time in cart view
  model.updateDeliveryTime();
};

export const controlUserInfoOnClick = function () {
  try {
    // Add parameter to the browser URL
    const urlParams = new URL(window.location.href);
    urlParams.searchParams.set("openCart", "true");
    urlParams.searchParams.delete("product-id");
    urlParams.searchParams.delete("search");
    const idMode = urlParams.searchParams.get("mode");

    // Detect auth state
    const isLoggedIn = cartView.isUserAuthenticatedFromDOM();

    // Adds an entry to the browser's session history stack
    history.replaceState(null, "", urlParams);

    // Load user info
    if (isLoggedIn) {
      // Load authenticated user data from the DOM
      model.loadLoggedInUserFromDOM();
    }

    // Determine which user info to use
    const userInfoToRender = isLoggedIn
      ? model.state.loggedInUserInfo
      : model.state.defaultUserInfo;

    // Validate required fields based on mode
    let hasRequiredFields;
    if (isLoggedIn) {
      hasRequiredFields =
        userInfoToRender &&
        userInfoToRender.firstName &&
        userInfoToRender.lastName &&
        userInfoToRender.phoneNumber &&
        userInfoToRender.email &&
        (idMode === "pickup" ? true : userInfoToRender.address);
    } else {
      hasRequiredFields =
        userInfoToRender &&
        userInfoToRender.name &&
        userInfoToRender.phoneNumber &&
        userInfoToRender.email &&
        (idMode === "pickup" ? true : userInfoToRender.address);
    }

    // Handle mode-specific flow
    if (idMode === "pickup") {
      if (hasRequiredFields) {
        // Render user details for pickup
        deliveryView.renderDataPickup(userInfoToRender);

        // Initialize submit button on page load
        deliveryView.initSubmitBtn();

        deliveryView.toggleSubmitButton();

        // Update pickup details, then render the updated data in the checkout summary
        controlUpdateCheckout();

        // Open checkout modal
        cartView.openModalCheckout();
      } else {
        // Render user details for pickup
        deliveryView.renderDataPickup(userInfoToRender);

        // Render service modal
        deliveryView.openModalServicePickup();

        // Initialize submit button on page load
        deliveryView.initSubmitBtn();

        deliveryView.toggleSubmitButton();
      }
    } else if (hasRequiredFields) {
      // Render user details for delivery
      deliveryView.renderDataDelivery(userInfoToRender);

      // Autocomplete delivery address
      autocompleteView.handlerSearchAddress(autoCompleteAddress);

      // Initialize submit button on page load
      deliveryView.initSubmitBtn();

      deliveryView.toggleSubmitButton();

      // Update delivery details, then render the updated data in the checkout summary
      controlUpdateCheckout();

      // Open checkout modal
      cartView.openModalCheckout();
    } else {
      // Render user details for delivery
      deliveryView.renderDataDelivery(userInfoToRender);

      // Render service modal
      deliveryView.openModalServiceDelivery();

      // Autocomplete delivery address
      autocompleteView.handlerSearchAddress(autoCompleteAddress);

      // Initialize submit button on page load
      deliveryView.initSubmitBtn();

      deliveryView.toggleSubmitButton();
    }
  } catch (err) {
    // Capture and send the error to Sentry
    Sentry.captureException(err);

    mainView.renderError();
  }
};

// Call init function as soon as the program starts
// Use await instead of then
const init = function () {
  // Re-format server-rendered amounts to the browser-detected locale
  mainView.formatCurrencyElements();

  // Enforce fulfillment mode based on store capabilities.
  // Stop while redirecting: the current URL may still carry an unsupported mode,
  // and requests started here would be cancelled by the navigation anyway.
  if (enforceFulfillmentMode()) {
    return;
  }

  // Get state
  const parsedUrl = new URL(window.location.href);
  const idMode = parsedUrl.searchParams.get("mode");

  // Render fulfillment selector based on supported modes
  initFulfillmentSelector();

  // Keep URL params in sync with modal state
  initModalUrlCleanup();

  // Get store opening status on main page
  const p1 = getStoreOpenings().then(() => {
    mainView.renderData(
      new Date(),
      model.state.openings.closingDays,
      model.state.openings.deliveryTimes,
      model.state.openings.timezone,
    );
    cartView.applyAuthUiState();
    controlInitCheckout();
  });

  const p2 = p1
    .then(() => {
      // Get ID of store
      const ID_STORE_LOCATION = mainView.getStoreLocationId();
      // Load cart
      return idMode === "pickup"
        ? model.loadCart("pickup", ID_STORE_LOCATION)
        : model.loadCart("delivery", ID_STORE_LOCATION);
    })
    .catch(() => {
      cartView.renderCartError();
    });

  p2.then(() => {
    // Get cart content
    const cartCount = model.state.cartQuantity;

    if (typeof cartCount !== "undefined") {
      document.querySelector(".cart-count").textContent = cartCount.toString();
    }

    // Render cart
    cartView.renderData(model.state.cart, idMode);

    // Attach event listener to product in cart
    productView.renderOnClickUpdate(controlProductOnClickUpdate);
    cartView.handlerRemoveCart(controlRemoveCart);

    // Resolve URL-driven actions after async data is ready (no load event needed)
    if (parsedUrl.searchParams.has("product-id")) {
      controlProductOnLoad();
    }
    if (parsedUrl.searchParams.has("search")) {
      controlSearchItemOnLoad();
    }
  });
  mainView.watchCategory();
  // Adjust padding based on category count
  mainView.adjustNavbarPadding();
  mainView.fireWindowSize();
  mainView.resizeCategoryHeader();
  mainView.toggleMenu();
  mainView.scrollToSection();
  // Initial check to display the carousel buttons correctly on page load
  mainView.updateCarouselBtn();
  mainView.handleCarouselBtnClick();
  mainView.handleCarouselBtnDisplay();
  productView.renderOnClick(controlProductOnClick);
  searchItemView.search(controlSearchItem);
  productView.increaseItems();
  productView.decreaseItems();
  deliveryView.closeModalService();
  cartView.handlerCheckout(controlCreateOrder);
  cartView.handlerPaymentMethodChange();
  cartView.editLocation();
  cartView.tooltipHandleDeliveryFee();
  mainView.tooltipHandleTime();
  cartView.initCheckoutBtn(idMode);
  // Add tip to cart
  cartView.addTip();
  idMode === "pickup"
    ? deliveryView.handlerPickupFormSubmit(controlUpdateCheckout)
    : deliveryView.handlerDeliveryFormSubmit(controlUpdateCheckout);
  cartView.renderPaymentOption(idMode);
  cartView.addHandlerDeliveryTimeChange(controlUpdateDeliveryTime);
  cartView.closeCart();
  deliveryView.renderAddress(controlUserInfoOnClick);
  mainView.closeModalOnEscape();
  searchItemView.renderOnClickSearch();
  mainView.closeModalError();
  // Toggle between pickup and delivery views
  toggleService();
  mainView.watchImg();
  cartView.watchTip();
  mainView.initPhoneNumberLogic();
};

// Create an async function to start the application
const startApp = async () => {
  try {
    // Wait for i18next to be fully initialized
    await i18nPromise;

    // Now that translations are ready, run the main app logic
    init();
  } catch (error) {
    // Capture and send the error to Sentry
    Sentry.captureException(error);

    console.error("Failed to initialize the application:", error);
  }
};

// Start the application
startApp();
